import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { type SponsorLogo, useSponsorModule } from 'src/lib/player/store';
import { useIsMobile } from 'src/lib/player/utils/hooks';
import { useElementDimensions } from 'src/lib/player/utils/useElementDimensions';
import {
  centerPinned,
  fitPrefix,
  type LogoBox,
  renderedWidth,
  sizesPending,
  UNMEASURED,
  useNaturalSizes,
  useRotatingWindow,
} from 'src/lib/sponsorLogos';

const FADE_DURATION_MS = 300;
const LOGO_GAP_PX = 10; // Gap between logos (must match CSS)
const LOGO_BOX: LogoBox = { maxWidth: 300, maxHeight: 100 }; // must match CSS
const LOGO_BOX_MOBILE: LogoBox = { maxWidth: 192, maxHeight: 64 }; // must match CSS

export const SponsorLogos: FC = () => {
  const sponsorModule = useSponsorModule();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const [isVisible, setIsVisible] = useState(true);
  const [displayedLogos, setDisplayedLogos] = useState<Array<SponsorLogo>>([]);

  const logos = sponsorModule?.enabled ? sponsorModule.logos : null;
  const naturalSizes = useNaturalSizes(logos);
  const isMobile = useIsMobile();
  const pending = sizesPending(logos, naturalSizes);

  const box = isMobile ? LOGO_BOX_MOBILE : LOGO_BOX;
  const widthOf = useCallback(
    (logo: SponsorLogo) => renderedWidth(naturalSizes[logo.id] ?? UNMEASURED, box),
    [naturalSizes, box],
  );

  const fit = useCallback(
    (order: ReadonlyArray<SponsorLogo>) =>
      pending ? [] : fitPrefix(order, widthOf, LOGO_GAP_PX, containerWidth),
    [pending, widthOf, containerWidth],
  );

  const selectedLogos = useRotatingWindow(logos, fit);

  const alignment = sponsorModule?.enabled ? sponsorModule.alignment : 'left';
  const arrangedLogos = useMemo(
    () =>
      centerPinned(
        selectedLogos.filter((logo) => logo.imageUrls?.logo != null),
        alignment,
      ),
    [selectedLogos, alignment],
  );

  // Handle logo changes with fade transition
  useLogoTransition({
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded: !pending,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  });

  const handleSponsorLogoClick = useCallback(() => {
    trackEvent('logo_click', { logo: 'sponsor' });
  }, []);

  if (sponsorModule == null || !sponsorModule.enabled) return null;

  return (
    <div
      ref={containerRef}
      className="sponsor-logos-container"
      style={{
        justifyContent: sponsorModule.alignment || 'left',
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
      }}
    >
      {displayedLogos.map((logo) => (
        <a
          key={logo.id}
          href={logo.url}
          title={logo.title}
          className="sponsor-logo-button"
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSponsorLogoClick}
        >
          <img src={logo.imageUrls?.logo} alt={logo.altText || logo.title || 'Sponsor logo'} />
        </a>
      ))}
    </div>
  );
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Handle fade transitions when logos change
 */
function useLogoTransition({
  arrangedLogos,
  displayedLogos,
  allWidthsLoaded,
  containerWidth,
  setDisplayedLogos,
  setIsVisible,
}: {
  arrangedLogos: Array<SponsorLogo>;
  displayedLogos: Array<SponsorLogo>;
  allWidthsLoaded: boolean;
  containerWidth: number;
  setDisplayedLogos: (logos: Array<SponsorLogo>) => void;
  setIsVisible: (visible: boolean) => void;
}) {
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Wait for container width and all image widths before showing
    if (!allWidthsLoaded || containerWidth <= 0) return;

    if (arrangedLogos.length === 0) {
      setDisplayedLogos([]);
      hasInitializedRef.current = false;
      return;
    }

    // First time showing logos - show immediately without fade
    if (!hasInitializedRef.current) {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
      hasInitializedRef.current = true;
      return;
    }

    // Check if the set of logos changed (ignore reordering)
    const sameLogos = areSameLogoSets(displayedLogos, arrangedLogos);

    if (sameLogos) {
      // Same logos, just reordered - update without fade
      setDisplayedLogos(arrangedLogos);
      return;
    }

    // Different logos - fade out, then show new ones
    setIsVisible(false);
    const timeoutId = setTimeout(() => {
      setDisplayedLogos(arrangedLogos);
      setIsVisible(true);
    }, FADE_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [
    arrangedLogos,
    displayedLogos,
    allWidthsLoaded,
    containerWidth,
    setDisplayedLogos,
    setIsVisible,
  ]);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two logo arrays contain the same logos (ignoring order)
 */
function areSameLogoSets(logosA: Array<SponsorLogo>, logosB: Array<SponsorLogo>): boolean {
  if (logosA.length !== logosB.length) return false;
  const idsA = new Set(logosA.map((l) => l.id));
  return logosB.every((logo) => idsA.has(logo.id));
}
